import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Megaphone, Users, Palette, Sparkles } from 'lucide-react';
import AdsCampaigns from './ads/AdsCampaigns';
import AdsAudiences from './ads/AdsAudiences';
import AdsCreatives from './ads/AdsCreatives';
import AdsFinalSets from './ads/AdsFinalSets';

const tabs = [
  { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
  { id: 'audiences', label: 'Audiences', icon: Users },
  { id: 'creatives', label: 'Creatives', icon: Palette },
  { id: 'final-ad-sets', label: 'Final Ad Sets', icon: Sparkles },
];

export default function AdsHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('campaigns');
  const [config, setConfig] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);

  const hasFullAccess = user?.email?.endsWith('@thelocaljewel.com') || user?.role === 'admin';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [cfgRes, campRes, audRes, crRes] = await Promise.all([
        api.get('/ads/config'),
        api.get('/ads/campaigns'),
        api.get('/ads/audiences'),
        api.get('/ads/creatives'),
      ]);
      setConfig(cfgRes.data);
      setCampaigns(campRes.data);
      setAudiences(audRes.data);
      setCreatives(crRes.data);
    } catch (err) {
      toast.error('Failed to load ads data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const tabContent = {
    campaigns: <AdsCampaigns items={campaigns} audiences={audiences} briefs={[]} config={config} reload={loadData} hasFullAccess={hasFullAccess} />,
    audiences: <AdsAudiences items={audiences} config={config} reload={loadData} hasFullAccess={hasFullAccess} />,
    creatives: <AdsCreatives items={creatives} config={config} reload={loadData} hasFullAccess={hasFullAccess} user={user} />,
    'final-ad-sets': <AdsFinalSets />,
  };

  if (loading && !config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-zinc-400">Loading Ads Hub...</div>
      </div>
    );
  }

  return (
    <div className="space-y-0" data-testid="ads-hub">
      {/* Inspirational Banner */}
      <div className="mb-6 bg-zinc-900 rounded-xl px-6 py-5 text-white" data-testid="ads-inspiration-banner">
        <p className="text-sm leading-relaxed text-zinc-300">
          The winning path to <span className="text-white font-semibold">100 rings/month</span> is not one magical campaign &mdash; it's a <span className="text-white font-semibold">creative machine</span> anchored to one clear belief:
        </p>
        <p className="mt-3 text-base font-medium text-amber-400 leading-relaxed" style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic' }}>
          "Rings are emotional purchases &mdash; but they are also engineered products. You want certified quality, fair pricing, attention to detail, and lifetime support when real life happens."
        </p>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">Ads</h1>
        <p className="text-sm text-zinc-500 mt-1">Manage campaigns, audiences, and creatives</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 p-1 bg-zinc-100 rounded-xl mb-6 overflow-x-auto" data-testid="ads-tab-nav">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              data-testid={`ads-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                isActive
                  ? 'bg-white text-zinc-900 shadow-sm'
                  : 'text-zinc-500 hover:text-zinc-700 hover:bg-white/50'
              }`}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div data-testid={`ads-content-${activeTab}`}>
        {tabContent[activeTab]}
      </div>
    </div>
  );
}
